import { useEffect, useMemo, useState, type ReactNode } from "react";

import type { Locale, ProductCatalogItem } from "@/catalog";
import { CompanyLogo } from "@/components/CompanyLogo";
import ShareOpportunity from "@/components/ShareOpportunity";
import UnlockReceipt from "@/components/UnlockReceipt";
import type { CopyText } from "@/copy";
import { firstSentence, outcomeToken, sourceStatusToken, verdictToken } from "@/format";
import type { UnlockReceiptData } from "@/unlock-receipt";

import type { OpportunityAnalysisPayload } from "./analysis";
import { Chapter } from "./Chapter";

export type ChapterId =
  | "verdict"
  | "outcome"
  | "bet"
  | "move"
  | "test"
  | "evidence"
  | "playbook"
  | "prompt";

const PROMPT_PREVIEW = 640;

function Field({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === false) return null;
  if (typeof children === "string" && !children.trim()) return null;
  return (
    <div className="field-block">
      <h3>{label}</h3>
      {typeof children === "string" ? <p>{children}</p> : children}
    </div>
  );
}

function playbookIntentLabel(
  intent: OpportunityAnalysisPayload["validationPlan"][number]["intent"],
  t: CopyText,
): string {
  if (intent === "trigger") return t.playbookIntentTrigger;
  if (intent === "evidence") return t.playbookIntentEvidence;
  if (intent === "delivery") return t.playbookIntentDelivery;
  if (intent === "commitment") return t.playbookIntentCommitment;
  return t.playbookIntentDecision;
}

function openChapter(id: string): void {
  const el = document.getElementById(id);
  if (el instanceof HTMLDetailsElement) el.open = true;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
}

function useChapterSpy(ids: string[]): string {
  const [active, setActive] = useState(ids[0] ?? "");
  const key = ids.join("|");
  useEffect(() => {
    if (!key) return;
    const list = key.split("|");
    const sync = () => {
      const offset = 140;
      const atEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 24;
      if (atEnd) {
        setActive(list[list.length - 1] ?? "");
        return;
      }
      let current = list[0] ?? "";
      for (const id of list) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= offset) current = id;
      }
      setActive(current);
    };
    sync();
    window.addEventListener("scroll", sync, { passive: true });
    return () => window.removeEventListener("scroll", sync);
  }, [key]);
  return active;
}

export function DecisionStrip({
  item,
  locale,
  t,
  analysis,
  isLocked,
  priceLabel,
  copyState,
  onCopyPrompt,
  onJumpPrompt,
}: {
  item: ProductCatalogItem;
  locale: Locale;
  t: CopyText;
  analysis: OpportunityAnalysisPayload | null;
  isLocked: boolean;
  priceLabel?: string;
  copyState: "idle" | "copied" | "error";
  onCopyPrompt?: () => void;
  onJumpPrompt?: () => void;
}) {
  const decision = analysis?.verdict.decision;
  return (
    <div className={`decision-strip${decision ? ` is-${decision}` : ""}${isLocked ? " is-locked" : ""}`}>
      <div className="strip-identity">
        <CompanyLogo item={item} />
        <div>
          <p className="strip-kicker">{item.batch ?? t.sourceSnapshot}</p>
          <h1>{item.name}</h1>
        </div>
      </div>
      {decision && analysis ? (
        <div className="strip-verdict">
          <strong className={`verdict-pill verdict-${decision}`}>{verdictToken(decision, locale)}</strong>
          <span>{t.verdictConfidence}: {outcomeToken(analysis.verdict.confidence, locale)}</span>
        </div>
      ) : isLocked && priceLabel ? (
        <div className="strip-verdict">
          <span className="strip-price-label">{t.priceNow}</span>
          <strong className="strip-price">{priceLabel}</strong>
        </div>
      ) : null}
      {!isLocked && onCopyPrompt ? (
        <div className="strip-actions">
          <button type="button" className="button button-accent strip-copy" onClick={onCopyPrompt}>
            {copyState === "copied" ? `✓ ${t.copied}` : copyState === "error" ? t.copyFailed : t.copyPrompt}
          </button>
          {onJumpPrompt ? (
            <button type="button" className="strip-jump" onClick={onJumpPrompt}>{t.chapterPrompt} ↓</button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function CompanyMeta({ item, locale, t }: { item: ProductCatalogItem; locale: Locale; t: CopyText }) {
  const founders = item.reportedFounders.map(({ name }) => name).join(", ");
  const captured = new Intl.DateTimeFormat(locale === "pt" ? "pt-BR" : "en-US", { dateStyle: "medium" }).format(new Date(item.source.fetchedAt));
  const parts = [
    item.batch,
    sourceStatusToken(item.reportedStatus, locale),
    item.reportedLocation,
    founders || null,
  ].filter((value): value is string => Boolean(value) && value !== "—");
  return (
    <ul className="company-meta">
      {parts.map((part, index) => <li key={`${index}-${part}`}>{part}</li>)}
      <li>
        <a className="source-link" href={item.source.url} target="_blank" rel="noreferrer" title={`${t.updated} ${captured}`}>
          {t.openSourceShort} ↗
        </a>
      </li>
    </ul>
  );
}

export function Briefing({
  item,
  locale,
  t,
  analysis,
  opportunity,
  fullPrompt,
  copyState,
  onCopyPrompt,
  unlockReceipt,
  isDraft,
}: {
  item: ProductCatalogItem;
  locale: Locale;
  t: CopyText;
  analysis: OpportunityAnalysisPayload;
  opportunity: NonNullable<ProductCatalogItem["opportunity"]>;
  fullPrompt?: string;
  copyState: "idle" | "copied" | "error";
  onCopyPrompt: () => void;
  unlockReceipt: UnlockReceiptData | null;
  isDraft: boolean;
}) {
  const [promptOpen, setPromptOpen] = useState(false);
  const chapters = useMemo(() => {
    const items: Array<{ id: ChapterId; label: string }> = [
      { id: "verdict", label: t.chapterVerdict },
    ];
    if (analysis.historicalOutcome.summary) items.push({ id: "outcome", label: t.chapterOutcome });
    if (analysis.thesis) items.push({ id: "bet", label: t.chapterBet });
    if (analysis.initialOffer) items.push({ id: "move", label: t.chapterMove });
    items.push({ id: "test", label: t.chapterTest });
    if (analysis.researchSources.length > 0) items.push({ id: "evidence", label: t.chapterEvidence });
    if (analysis.validationPlan.length > 0) items.push({ id: "playbook", label: t.chapterPlaybook });
    if (fullPrompt) items.push({ id: "prompt", label: t.chapterPrompt });
    return items;
  }, [analysis, fullPrompt, t]);
  const ids = useMemo(() => chapters.map((chapter) => chapter.id), [chapters]);
  const active = useChapterSpy(ids);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash || !ids.includes(hash as ChapterId)) return;
    window.requestAnimationFrame(() => openChapter(hash));
  }, [ids]);

  const goTo = (id: string) => {
    openChapter(id);
    const url = new URL(window.location.href);
    url.hash = id;
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const verdictTakeaway = firstSentence(analysis.verdict.summary);
  const verdictRest = analysis.verdict.summary.slice(verdictTakeaway.length).trim();
  const outcomeTakeaway = analysis.historicalOutcome.summary
    ? firstSentence(analysis.historicalOutcome.summary)
    : "";
  const outcomeRest = analysis.historicalOutcome.summary.slice(outcomeTakeaway.length).trim();
  const promptLong = Boolean(fullPrompt && fullPrompt.length > PROMPT_PREVIEW);
  const promptText = fullPrompt && (!promptLong || promptOpen)
    ? fullPrompt
    : fullPrompt
      ? `${fullPrompt.slice(0, PROMPT_PREVIEW).trimEnd()}…`
      : "";

  return (
    <div className="briefing-shell">
      <nav className="chapter-nav" aria-label={t.chapterNavLabel}>
        {chapters.map((chapter) => (
          <a
            key={chapter.id}
            href={`#${chapter.id}`}
            className={active === chapter.id ? "is-active" : undefined}
            aria-current={active === chapter.id ? "location" : undefined}
            onClick={(event) => {
              event.preventDefault();
              goTo(chapter.id);
            }}
          >
            {chapter.label}
          </a>
        ))}
      </nav>
      <div className="briefing-document">
        {item.sourceOneLiner ? <p className="detail-one-liner">{item.sourceOneLiner}</p> : null}
        <CompanyMeta item={item} locale={locale} t={t} />
        <p className="editorial-note">{t.curationBoundary}</p>

        <Chapter id="verdict" title={t.verdictTitle} takeaway={verdictTakeaway} defaultOpen t={t}>
          {verdictRest ? <p>{verdictRest}</p> : null}
          <ul className="prose-list">
            {analysis.verdict.reasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        </Chapter>

        {analysis.historicalOutcome.summary ? (
          <Chapter
            id="outcome"
            title={t.outcomeTitle}
            takeaway={outcomeTakeaway}
            defaultOpen
            t={t}
            extra={(
              <div className="fact-chips">
                <span><small>{t.outcomeDisposition}</small><strong>{outcomeToken(analysis.historicalOutcome.disposition, locale)}</strong></span>
                <span><small>{t.outcomeEvidence}</small><strong>{outcomeToken(analysis.historicalOutcome.dispositionConfidence, locale)}</strong></span>
                <span><small>{t.outcomeCause}</small><strong>{outcomeToken(analysis.historicalOutcome.causalStatus, locale)}</strong></span>
              </div>
            )}
          >
            {outcomeRest ? <p>{outcomeRest}</p> : null}
            {analysis.historicalOutcome.factors.length > 0 && (
              <ul className="prose-list">
                {analysis.historicalOutcome.factors.map((factor) => <li key={factor}>{factor}</li>)}
              </ul>
            )}
            {analysis.historicalOutcome.sourceUrls.length > 0 && (
              <div className="source-row">
                {analysis.historicalOutcome.sourceUrls.map((url, index) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer">{t.sourceLabel} {index + 1} ↗</a>
                ))}
              </div>
            )}
          </Chapter>
        ) : null}

        {analysis.thesis ? (
          <Chapter id="bet" title={opportunity.title[locale]} takeaway={firstSentence(analysis.thesis)} t={t}>
            <Field label={t.currentThesis}>{analysis.thesis}</Field>
            <div className="field-pair">
              <Field label={t.buyerTitle}>{analysis.targetCustomer}</Field>
              <Field label={t.purchaseTrigger}>{analysis.purchaseTrigger}</Field>
            </div>
            <Field label={t.historicalContext}>{analysis.historicalContext}</Field>
          </Chapter>
        ) : null}

        {analysis.initialOffer ? (
          <Chapter id="move" title={t.commercialTitle} takeaway={firstSentence(analysis.initialOffer)} t={t}>
            <Field label={t.initialOffer}>{analysis.initialOffer}</Field>
            <div className="field-pair">
              <Field label={t.businessModel}>{analysis.businessModel}</Field>
              <Field label={t.distribution}>{analysis.distribution}</Field>
            </div>
            {analysis.risks.length > 0 && (
              <aside className="risk-list">
                <h3>{t.riskTitle}</h3>
                <ul className="prose-list">
                  {analysis.risks.map((risk) => <li key={risk}>{risk}</li>)}
                </ul>
              </aside>
            )}
          </Chapter>
        ) : null}

        <Chapter
          id="test"
          title={t.mechanicsTitle}
          t={t}
          extra={(
            <div className="test-takeaways">
              <p className="chapter-takeaway"><span>{t.firstTest}.</span> {firstSentence(analysis.smallestTest)}</p>
              <p className="chapter-kill"><span>{t.kill}.</span> {firstSentence(analysis.killCriterion)}</p>
            </div>
          )}
        >
          <ol className="mechanic-list">
            <li><span>01</span><div><h3>{t.problem}</h3><p>{analysis.problem}</p></div></li>
            <li><span>02</span><div><h3>{t.whyNow}</h3><p>{analysis.whyNow}</p></div></li>
            <li><span>03</span><div><h3>{t.wedge}</h3><p>{analysis.wedge}</p></div></li>
            <li><span>04</span><div><h3>{t.coreLoop}</h3><p>{analysis.coreLoop}</p></div></li>
            <li><span>05</span><div><h3>{t.firstTest}</h3><p>{analysis.smallestTest}</p></div></li>
            <li className="is-kill"><span>06</span><div><h3>{t.kill}</h3><p>{analysis.killCriterion}</p></div></li>
          </ol>
          {analysis.nonGoals.length > 0 && (
            <div className="non-goals">
              <strong>{t.nonGoals}</strong>
              {analysis.nonGoals.map((value) => <span key={value}>{value}</span>)}
            </div>
          )}
        </Chapter>

        {analysis.researchSources.length > 0 ? (
          <Chapter
            id="evidence"
            title={t.evidenceLedger}
            takeaway={locale === "pt"
              ? `${analysis.researchSources.length} fontes revisáveis. Limites no final do capítulo.`
              : `${analysis.researchSources.length} reviewable sources. Limits sit at the end of this chapter.`}
            t={t}
          >
            <ul className="evidence-list">
              {analysis.researchSources.map((source, index) => (
                <li key={`${source.url}-${index}`}>
                  <div className="evidence-meta">
                    <span>{source.type.replaceAll("_", " ")}</span>
                    <small>{source.publisher}</small>
                  </div>
                  <h3>{source.title}</h3>
                  <ul className="prose-list">
                    {source.supports.map((claim) => <li key={claim}>{claim}</li>)}
                  </ul>
                  <a href={source.url} target="_blank" rel="noreferrer">{locale === "pt" ? "Abrir fonte" : "Open source"} ↗</a>
                </li>
              ))}
            </ul>
            {analysis.evidenceLimits.length > 0 && (
              <aside className="limits-note">
                <h3>{t.limitsTitle}</h3>
                <ul className="prose-list">
                  {analysis.evidenceLimits.map((limit) => <li key={limit}>{limit}</li>)}
                </ul>
              </aside>
            )}
          </Chapter>
        ) : null}

        {analysis.validationPlan.length > 0 ? (
          <Chapter id="playbook" title={t.validationTitle} takeaway={t.validationBody} defaultOpen t={t}>
            <ol className="playbook-list">
              {analysis.validationPlan.map((step, index) => (
                <li key={step.intent}>
                  <article className={`playbook-phase is-${step.intent}`}>
                    <header className="playbook-phase-head">
                      <span className="playbook-phase-number">{String(index + 1).padStart(2, "0")}</span>
                      <div className="playbook-phase-heading">
                        <span>{step.window} · {playbookIntentLabel(step.intent, t)}</span>
                        <h3>{step.goal}</h3>
                      </div>
                    </header>
                    <div className="playbook-phase-fields">
                      <div className="playbook-phase-field">
                        <strong>{t.validationArtifact}</strong>
                        <p>{step.artifact}</p>
                      </div>
                      <div className="playbook-phase-field">
                        <strong>{t.validationMetric}</strong>
                        <p>{step.metric}</p>
                      </div>
                      <div className="playbook-phase-field is-gate">
                        <strong>{t.validationGateLong}</strong>
                        <p>{step.gate}</p>
                      </div>
                    </div>
                    <details className="playbook-actions" open={opportunity.freeSample}>
                      <summary>{t.validationActions}</summary>
                      <ul className="prose-list">
                        {step.actions.map((action) => <li key={action}>{action}</li>)}
                      </ul>
                    </details>
                  </article>
                </li>
              ))}
            </ol>
          </Chapter>
        ) : null}

        {fullPrompt ? (
          <section id="prompt" className="prompt-chapter">
            <header className="prompt-chapter-head">
              <div>
                <h2>{t.promptTitle}</h2>
                <p>{t.promptBody}</p>
              </div>
              <button type="button" className="button button-accent copy-button" onClick={onCopyPrompt}>
                {copyState === "copied" ? `✓ ${t.copied}` : copyState === "error" ? t.copyFailed : t.copyPrompt}
              </button>
            </header>
            <div className="prompt-panel">
              <div className="prompt-status">
                <span>{isDraft ? t.draftState : opportunity.freeSample ? t.freeSample : t.unlock}</span>
                <strong>{isDraft ? "OPEN" : opportunity.freeSample ? "$0" : "✓"}</strong>
              </div>
              <pre>{promptText}</pre>
              {promptLong ? (
                <button type="button" className="prompt-expand" onClick={() => setPromptOpen((open) => !open)}>
                  {promptOpen ? t.hideFullPrompt : t.showFullPrompt}
                </button>
              ) : null}
              <p className="unlock-note">{isDraft ? t.draftPromptNote : opportunity.freeSample ? t.unlockedFree : t.unlockedPaid}</p>
              {unlockReceipt && <UnlockReceipt locale={locale} receipt={unlockReceipt} />}
              <ShareOpportunity
                locale={locale}
                opportunitySlug={item.slug}
                companyName={item.name}
                placement={isDraft || opportunity.freeSample ? "preview" : "purchase"}
                className="prompt-share"
              />
            </div>
          </section>
        ) : (
          <p className="unlock-note">{t.checkoutError}</p>
        )}
      </div>
    </div>
  );
}
